import * as React from 'react'

import {
  ChartNoAxesGanttIcon,
  EyeOffIcon,
  HouseIcon,
  TruckIcon,
} from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import DynamicOverflowList from '~/client/src/shared/components/DynamicOverflowList/DynamicOverflowList'
import { WorkflowIcon } from '~/client/src/shared/icons'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { IMapViewPublishedTo } from './MapViewsSidebarListItem'

// Figma 12507:192398 (compact) and 12513:194085 (overflow).

export type PublishToAppKey = 'home' | 'workflows' | 'deliveries' | 'schedule'

export const ALL_PUBLISH_TO_APP_KEYS: PublishToAppKey[] = [
  'home',
  'workflows',
  'deliveries',
  'schedule',
]

interface IPublishToAppDescriptor {
  /** Lazy because Localization isn't ready at module-evaluation time. */
  label: () => string
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
}

export const PUBLISH_TO_APP_DESCRIPTORS: Record<
  PublishToAppKey,
  IPublishToAppDescriptor
> = {
  home: {
    label: () => Localization.translator.home,
    Icon: HouseIcon,
  },
  workflows: {
    label: () => Localization.translator.workflows,
    Icon: WorkflowIcon,
  },
  deliveries: {
    label: () => Localization.translator.deliveries,
    Icon: TruckIcon,
  },
  schedule: {
    label: () => Localization.translator.schedule,
    Icon: ChartNoAxesGanttIcon,
  },
}

// Reserved width for the "+N more" indicator (12px Inter Medium fits in ~52px even at 99).
const MORE_BUTTON_WIDTH = 56

// Sizes items against Button's content area (inside px-2.5) so the chip row aligns
// with scope/capability sibling rows.
const PADDING_WIDTH = 24

const ITEM_LABEL = 'tw:text-label-md tw:text-foreground'

// Mirrors ghost Button size="default" so read-only and admin (Button) modes share the same grid.
const READONLY_WRAPPER =
  'tw:inline-flex tw:items-center tw:gap-1.5 tw:h-8 tw:px-2.5 tw:max-w-full'

interface IProps {
  publishedTo: IMapViewPublishedTo
  /** Apps absent here never appear in the chip row OR popup. */
  availableApps: PublishToAppKey[]
  /** Omit to render read-only; otherwise receives the chip's DOM node for popover anchoring. */
  onClick?: (anchor: HTMLElement) => void
}

export const PublishToChipRow: React.FC<IProps> = ({
  publishedTo,
  availableApps,
  onClick,
}) => {
  // Trailing comma on every item except last source — when overflow kicks in,
  // the new last visible chip retains its comma ("Home, Workflows, Deliveries +6 more").
  // Hooks before any early return (Rules of Hooks).
  const items = React.useMemo(
    () =>
      availableApps
        .filter(key => publishedTo[key])
        .map((key, idx, arr) => {
          const { label, Icon } = PUBLISH_TO_APP_DESCRIPTORS[key]
          const isLastSource = idx === arr.length - 1
          const text = isLastSource ? label() : `${label()},`
          return (
            <span
              key={key}
              className="tw:flex tw:items-center tw:gap-1.5 tw:shrink-0"
            >
              <Icon
                className="tw:size-4 tw:shrink-0 tw:text-muted-foreground"
                aria-hidden="true"
              />
              <span className={ITEM_LABEL}>{text}</span>
            </span>
          )
        }),
    [availableApps, publishedTo],
  )

  const handleClick = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation()
    onClick?.(e.currentTarget)
  }
  const stopPointerDown = (e: React.PointerEvent): void => e.stopPropagation()

  if (items.length === 0) {
    return onClick ? (
      <Button
        variant="ghost"
        onClick={handleClick}
        onPointerDown={stopPointerDown}
        aria-label={Localization.translator.publishTo}
        className="tw:max-w-full tw:text-muted-foreground"
      >
        <EyeOffIcon className="tw:size-4 tw:shrink-0" aria-hidden="true" />
        <span className="tw:text-label-md">
          {Localization.translator.notPublished}
        </span>
      </Button>
    ) : (
      <span className={`${READONLY_WRAPPER} tw:text-muted-foreground`}>
        <EyeOffIcon className="tw:size-4 tw:shrink-0" aria-hidden="true" />
        <span className="tw:text-label-md">
          {Localization.translator.notPublished}
        </span>
      </span>
    )
  }

  return (
    <DynamicOverflowList
      items={items}
      moreButtonWidth={MORE_BUTTON_WIDTH}
      paddingWidth={PADDING_WIDTH}
      shouldUseItemKey
      allowRecalculationForItems
      shouldIgnoreScrollBarWidth
    >
      {({
        visibleElements,
        overflowElements,
        containerRefSetter,
        setAdditionalElementsRef,
      }) => {
        // Measurement container must be `w-full` (not inline-flex on the chip itself) —
        // otherwise collapsing one item shrinks the chip, the algorithm sees a narrower
        // container next render, and collapse cascades.
        const innerContent = (
          <>
            {visibleElements}
            {overflowElements.length > 0 && (
              <span
                ref={setAdditionalElementsRef}
                className={`${ITEM_LABEL} tw:shrink-0`}
              >
                +{overflowElements.length} more
              </span>
            )}
          </>
        )

        return (
          <div ref={containerRefSetter} className="tw:flex tw:w-full">
            {onClick ? (
              <Button
                variant="ghost"
                onClick={handleClick}
                onPointerDown={stopPointerDown}
                aria-label={Localization.translator.publishTo}
                // justify-start (overrides Button's justify-center) pins items to the same column as siblings.
                className="tw:max-w-full tw:justify-start"
              >
                {innerContent}
              </Button>
            ) : (
              <span className={READONLY_WRAPPER}>{innerContent}</span>
            )}
          </div>
        )
      }}
    </DynamicOverflowList>
  )
}
